import { useRef } from 'react';
import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { createPortal } from 'react-dom';
import { getAnimation } from '../../../../helpers';
import { Deactivation, useTheme } from '../../../common';
import { usePlayer } from '../../PlayerProvider';
import { Mobile } from '../widget-types';
import { useFloating } from './FloatingProvider';
import { MinimizedPlayer } from './minimized-player';

export const FloatingMobile = () => {
  const refInner = useRef(null);
  const { font, displayDeactivation } = useSettings();
  const { showPlaylist, setShowPlaylist } = usePlayer();
  const { textColor, backgroundStyle } = useTheme();
  const { minimizePlayer } = useFloating();
  const mobileProps = {
    isFloating: true,
    _backgroundStyle: backgroundStyle,
    _textColor: textColor
  };

  return createPortal(
    <Container _font={font?.value || 'inherit'}>
      <PlayerContainer
        onTransitionEnd={() => {
          if (minimizePlayer) {
            setShowPlaylist(false);
          }
        }}
        _isHidden={minimizePlayer}
      >
        <Inner
          ref={refInner}
          onClick={(e) => {
            if (showPlaylist && e.target === refInner?.current) {
              setShowPlaylist(!showPlaylist);
            }
          }}
          _showPlaylist={showPlaylist}
          _isHidden={minimizePlayer}
        >
          <Show
            when={!!displayDeactivation && !minimizePlayer}
            fallback={<StyledMobile {...mobileProps} />}
          >
            <Deactivation containerStyle={{ width: '100%' }}>
              <DeactivationContainer>
                <StyledMobile {...mobileProps} />
              </DeactivationContainer>
            </Deactivation>
          </Show>
        </Inner>
      </PlayerContainer>
      <StyledMinimizedPlayer _isShow={minimizePlayer} isMobileView />
    </Container>,
    document.body
  );
};

const Container = styled.div<{
  _font: string;
}>`
  z-index: 999999999;
  width: 100vw;
  height: 0;
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  font-family: ${({ _font }) => _font};
`;

const PlayerContainer = styled.div<{
  _isHidden: boolean;
}>`
  position: relative;
  z-index: 1;
  height: fit-content;
  transition:
    ${getAnimation('translate', 'ease-out')},
    ${getAnimation('opacity', 'ease-out')};

  ${({ _isHidden }) =>
    _isHidden &&
    `
      opacity: 0;
      translate: 0 calc(100% + 26px);
    `};
`;

const Inner = styled.div<{
  _showPlaylist: boolean;
  _isHidden: boolean;
}>`
  width: 100%;
  height: fit-content;
  display: flex;
  align-items: flex-end;
  transition: ${getAnimation('background-color', 'ease-out')};

  ${({ _showPlaylist }) =>
    _showPlaylist &&
    `
      height: 100vh;
      @supports (height: 100dvh) {
        height: 100dvh;
      }
      &:hover {
        cursor: pointer;
      }
      & > *:first-child {
        cursor: default;
      }
    `}

  ${({ _showPlaylist, _isHidden }) =>
    !_isHidden &&
    _showPlaylist &&
    `
      background-color: rgba(17, 17, 17, 0.5);
    `}
`;

const StyledMobile = styled(Mobile)<{
  _textColor: string;
  _backgroundStyle: string;
}>`
  height: fit-content;
  max-height: calc(100vh - 64px);
  @supports (max-height: 100dvh) {
    max-height: calc(100dvh - 64px);
  }
  box-shadow: rgba(0, 0, 0, 0.15) 0 2px 15px;
  color: ${({ _textColor }) => _textColor};

  ${({ _backgroundStyle }) => _backgroundStyle}
`;

const StyledMinimizedPlayer = styled(MinimizedPlayer)<{
  _isShow: boolean;
}>`
  transition: ${getAnimation('opacity', 'ease-out')};
  opacity: ${({ _isShow }) => (_isShow ? 1 : 0)};
`;

const DeactivationContainer = styled.div`
  width: 100%;
`;
